import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { freelancerService } from '@/lib/api/services/freelancer.service'
import { useSearchGeniuses } from '@/lib/hooks/use-search-geniuses'
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile'
import { useClientProfileCompletion } from '@/lib/hooks/use-client-profile-completion'
import { useFreelancerSkills } from '@/lib/hooks/use-freelancer-skills'
import { ClientNextSteps } from '@/components/dashboard/client-next-steps'
import { FreelancerCard } from '@/components/freelancer/freelancer-card'
import { Input } from '@/components/ui/input'
import { Search, ArrowRight, SlidersHorizontal, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { SlideIn, FadeIn, StaggerContainer, StaggerItem, ViewportReveal } from '@/components/motion'

// Availability options
const AVAILABILITY_OPTIONS = [
  { id: 'full-time', label: 'Full-time' },
  { id: 'part-time', label: 'Part-time' },
  { id: 'contract', label: 'Contract' },
]

// Rate range options
const RATE_RANGES = [
  { id: '50-100', label: '$50-100/hr', min: 50, max: 100 },
  { id: '100-200', label: '$100-200/hr', min: 100, max: 200 },
  { id: '200+', label: '$200+/hr', min: 200, max: null },
]

export default function ClientDashboard() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { userId } = useAuth()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [showFilters, setShowFilters] = useState(false)

  // Advanced filters state
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>([])
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([])
  const [selectedRateRanges, setSelectedRateRanges] = useState<string[]>([])
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])

  // Fetch skills from database
  const { data: skills = [] } = useFreelancerSkills()

  // Client profile completion
  const { clientProfile } = useOwnClientProfile()
  const profileCompletion = useClientProfileCompletion(clientProfile)

  // Search functionality
  const {
    searchGeniuses,
    isSearching,
    currentResults,
    selectedSearchId,
    setSelectedSearchId
  } = useSearchGeniuses()

  const [activeSearchId, setActiveSearchId] = useState<string | null>(selectedSearchId)

  // Auto-search if query param is present
  useEffect(() => {
    const q = searchParams.get('q')
    if (q && !activeSearchId) {
      setSearchQuery(q)
      const searchId = searchGeniuses(q, {})
      setActiveSearchId(searchId)
      setSelectedSearchId(searchId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Fetch featured freelancers from backend API
  // Include userId in query key to ensure re-fetch after auth state changes on page reload
  const { data: freelancers = [], isLoading: isLoadingFeatured } = useQuery({
    queryKey: ['featured-freelancers', userId],
    queryFn: async () => {
      return await freelancerService.getFeaturedFreelancers(6)
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  })

  // Filter featured freelancers by selected category
  const filteredFeaturedFreelancers = useMemo(() => {
    if (selectedCategory === 'all') return freelancers
    return freelancers.filter(f => {
      const skills = Array.isArray(f.skills) ? (f.skills as string[]) : []
      return skills.some(skill =>
        skill.toLowerCase().includes(selectedCategory.replace('-', ' '))
      )
    })
  }, [freelancers, selectedCategory])

  // Get unique categories from featured freelancers' skills
  const categoryFilters = useMemo(() => {
    const categories = new Set<string>()
    freelancers.forEach(f => {
      const skills = Array.isArray(f.skills) ? (f.skills as string[]) : []
      skills.forEach(skill => categories.add(skill))
    })
    // Take first 5 unique skills as category filters
    const topCategories = Array.from(categories).slice(0, 5)
    return [
      { id: 'all', label: 'All' },
      ...topCategories.map(cat => ({
        id: cat.toLowerCase().replace(/\s+/g, '-'),
        label: cat,
      }))
    ]
  }, [freelancers])

  // Toggle filter functions
  const toggleExpertise = (skillName: string) => {
    setSelectedExpertise(prev =>
      prev.includes(skillName)
        ? prev.filter(s => s !== skillName)
        : [...prev, skillName]
    )
  }

  const toggleAvailability = (id: string) => {
    setSelectedAvailability(prev =>
      prev.includes(id)
        ? prev.filter(a => a !== id)
        : [...prev, id]
    )
  }

  const toggleRateRange = (id: string) => {
    setSelectedRateRanges(prev =>
      prev.includes(id)
        ? prev.filter(r => r !== id)
        : [...prev, id]
    )
  }

  const toggleLanguage = (language: string) => {
    setSelectedLanguages(prev =>
      prev.includes(language)
        ? prev.filter(l => l !== language)
        : [...prev, language]
    )
  }

  const clearAllFilters = () => {
    setSelectedExpertise([])
    setSelectedAvailability([])
    setSelectedRateRanges([])
    setSelectedLanguages([])
  }

  const hasActiveFilters = selectedExpertise.length > 0 || selectedAvailability.length > 0 || selectedRateRanges.length > 0 || selectedLanguages.length > 0

  // Build filters object from selected options
  const buildFilters = () => {
    const filters: {
      minHourlyRate?: number;
      maxHourlyRate?: number;
      skills?: string[];
      languages?: string[];
      availabilityStatuses?: string[];
    } = {}

    // Add skills filter
    if (selectedExpertise.length > 0) {
      filters.skills = selectedExpertise
    }

    // Map availability IDs to backend status values
    const availabilityMap: Record<string, string> = {
      'full-time': 'available',
      'part-time': 'part-time',
      'contract': 'available' // Treat contract as available
    }
    
    if (selectedAvailability.length > 0) {
      filters.availabilityStatuses = selectedAvailability
        .map(id => availabilityMap[id])
        .filter(Boolean) as string[]
    }

    // Add languages filter
    if (selectedLanguages.length > 0) {
      filters.languages = selectedLanguages
    }

    // Add rate range filter (combine selected ranges using union logic)
    if (selectedRateRanges.length > 0) {
      const selectedRates = RATE_RANGES.filter(r => selectedRateRanges.includes(r.id))
      
      // Find the overall min and max across all selected ranges (union of ranges)
      const allMins = selectedRates.map(r => r.min)
      const allMaxes = selectedRates.filter(r => r.max !== null).map(r => r.max!)
      const hasUnboundedMax = selectedRates.some(r => r.max === null)
      
      // Use the minimum of all minimums (union starts from lowest)
      filters.minHourlyRate = Math.min(...allMins)
      
      // Use the maximum of all maximums, or undefined if any range is unbounded
      if (hasUnboundedMax) {
        // If any range is unbounded (200+), don't set maxHourlyRate
        filters.maxHourlyRate = undefined
      } else if (allMaxes.length > 0) {
        filters.maxHourlyRate = Math.max(...allMaxes)
      }
    }

    return filters
  }

  const handleSearch = () => {
    const query = searchQuery.trim()
    if (!query) {
      // If no query, use a general search term
      const filters = buildFilters()
      const searchId = searchGeniuses('AI expert', filters)
      setActiveSearchId(searchId)
      setSelectedSearchId(searchId)
      return
    }
    
    const filters = buildFilters()
    const searchId = searchGeniuses(query, filters)
    setActiveSearchId(searchId)
    setSelectedSearchId(searchId)
  }

  // Apply filters - triggers a new search with current query and filters
  const handleApplyFilters = () => {
    handleSearch()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const searchResults = activeSearchId ? currentResults(activeSearchId) : null
  const hasResults = searchResults && searchResults.results.length > 0

  // Extract available languages from search results or featured freelancers
  // Note: featured_freelancer_profiles view doesn't include languages, so we extract from search results
  const availableLanguages = useMemo(() => {
    const languagesSet = new Set<string>()
    
    // Extract from search results if available
    if (searchResults && searchResults.results) {
      searchResults.results.forEach((f) => {
        // Languages field may exist in results even if not in type definition yet
        const resultWithLanguages = f as typeof f & { languages?: string[] | null }
        const langs = Array.isArray(resultWithLanguages.languages) ? resultWithLanguages.languages : []
        langs.forEach((lang: string) => {
          if (lang && lang.trim()) {
            languagesSet.add(lang.trim())
          }
        })
      })
    }
    
    // Fallback: common languages list
    if (languagesSet.size === 0) {
      const commonLanguages = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi']
      commonLanguages.forEach(lang => languagesSet.add(lang))
    }
    
    return Array.from(languagesSet).sort()
  }, [searchResults])

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Search Section */}
      <div
        className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
        }}
      >
        <div className="max-w-[900px] mx-auto">
          <SlideIn direction="down">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 text-center mb-3 sm:mb-4">
              Find Your AI Expert
            </h1>
          </SlideIn>
          <FadeIn delay={0.1}>
            <p className="text-base sm:text-lg text-slate-500 text-center mb-6 sm:mb-8 leading-relaxed px-2">
              Search our network of rigorously vetted AI consultants. Tell us what you need and we'll match you with the perfect expert.
            </p>
          </FadeIn>
        </div>

        {/* Profile Completion Next Steps - Full Width */}
        <div className="max-w-[1400px] px-4 sm:px-6 lg:px-8 mx-auto">
          <FadeIn delay={0.15}>
            <ClientNextSteps completion={profileCompletion} />
          </FadeIn>
        </div>

        <div className="max-w-[900px] mx-auto">
          {/* Search Box */}
          <SlideIn direction="up" delay={0.2}>
            <div className={`bg-white rounded-2xl sm:rounded-[20px] p-4 sm:p-6 lg:p-8 shadow-lg ${!profileCompletion.isComplete ? 'opacity-60' : ''}`}>
              {/* Mobile: stacked layout, Desktop: inline */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:relative">
                <div className="relative flex-1">
                  <Search className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={profileCompletion.isComplete ? handleKeyDown : undefined}
                    placeholder={profileCompletion.isComplete ? "Search for AI experts..." : "Complete your profile to search..."}
                    disabled={!profileCompletion.isComplete}
                    className="h-14 sm:h-[72px] pl-12 sm:pl-14 sm:pr-36 text-base rounded-xl sm:rounded-[15px] border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 disabled:cursor-not-allowed"
                  />
                  {/* Desktop button - positioned absolutely */}
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !profileCompletion.isComplete}
                    className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-[56px] px-6 lg:px-8 rounded-full bg-[#4669ed] hover:bg-[#3654c9] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold items-center gap-2 transition-colors"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="hidden lg:inline">Searching...</span>
                      </>
                    ) : (
                      <>
                        Search
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
                {/* Mobile button - full width below input */}
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !profileCompletion.isComplete}
                  className="sm:hidden h-12 px-6 rounded-full bg-[#4669ed] hover:bg-[#3654c9] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      Search
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </SlideIn>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 -mt-4 sm:-mt-8">
        {/* Refine Search Bar with Expandable Filters */}
        <SlideIn direction="up" delay={0.4}>
          <div
            className={`rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-5 sm:p-7 lg:p-10 mb-8 sm:mb-12 transition-all duration-300 ${!profileCompletion.isComplete ? 'opacity-60' : ''}`}
            style={{
              background: 'linear-gradient(100.97deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
          >
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <span className="text-white font-bold text-sm sm:text-base">
              {!profileCompletion.isComplete 
                ? 'Complete your profile to use filters' 
                : hasResults 
                  ? `${searchResults.results.length} experts found` 
                  : 'Refine Your Search'}
            </span>
            <div className="flex items-center gap-2 sm:gap-3">
              {hasActiveFilters && profileCompletion.isComplete && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-semibold transition-colors"
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Clear All</span>
                </button>
              )}
              <button
                onClick={() => profileCompletion.isComplete && setShowFilters(!showFilters)}
                disabled={!profileCompletion.isComplete}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full font-semibold text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  hasActiveFilters
                    ? 'bg-white text-[#2b42a3] hover:bg-slate-100'
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Advanced</span> Filters
                {hasActiveFilters && (
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-[#2b42a3] text-white text-xs font-bold">
                    {selectedExpertise.length + selectedAvailability.length + selectedRateRanges.length + selectedLanguages.length}
                  </span>
                )}
                {showFilters ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

              {/* Expandable Filters Panel */}
          {showFilters && profileCompletion.isComplete && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {/* Expertise Filter */}
                <div className="space-y-3">
                  <div className="text-white/95 text-sm font-semibold">Expertise</div>
                  <div className="flex flex-wrap gap-2">
                    {skills.slice(0, 8).map((skill) => {
                      const isSelected = selectedExpertise.includes(skill.name)
                      return (
                        <button
                          key={skill.id}
                          onClick={() => profileCompletion.isComplete && toggleExpertise(skill.name)}
                          disabled={!profileCompletion.isComplete}
                          className={`px-3 sm:px-[18px] py-2 sm:py-2.5 rounded-full border-2 text-xs sm:text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                            isSelected
                              ? 'bg-white border-white text-[#2b42a3]'
                              : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                          }`}
                        >
                          {skill.name}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Availability Filter */}
                <div className="space-y-3">
                  <div className="text-white/95 text-sm font-semibold">Availability</div>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABILITY_OPTIONS.map((option) => {
                      const isSelected = selectedAvailability.includes(option.id)
                      return (
                        <button
                          key={option.id}
                          onClick={() => profileCompletion.isComplete && toggleAvailability(option.id)}
                          disabled={!profileCompletion.isComplete}
                          className={`px-3 sm:px-[18px] py-2 sm:py-2.5 rounded-full border-2 text-xs sm:text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                            isSelected
                              ? 'bg-white border-white text-[#2b42a3]'
                              : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                          }`}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Rate Range Filter */}
                <div className="space-y-3">
                  <div className="text-white/95 text-sm font-semibold">Rate Range</div>
                  <div className="flex flex-wrap gap-2">
                    {RATE_RANGES.map((range) => {
                      const isSelected = selectedRateRanges.includes(range.id)
                      return (
                        <button
                          key={range.id}
                          onClick={() => profileCompletion.isComplete && toggleRateRange(range.id)}
                          disabled={!profileCompletion.isComplete}
                          className={`px-3 sm:px-[18px] py-2 sm:py-2.5 rounded-full border-2 text-xs sm:text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                            isSelected
                              ? 'bg-white border-white text-[#2b42a3]'
                              : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                          }`}
                        >
                          {range.label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Languages Filter */}
                <div className="space-y-3">
                  <div className="text-white/95 text-sm font-semibold">Languages</div>
                  <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                    {availableLanguages.length > 0 ? (
                      availableLanguages.map((language) => {
                        const isSelected = selectedLanguages.includes(language)
                        return (
                          <button
                            key={language}
                            onClick={() => profileCompletion.isComplete && toggleLanguage(language)}
                            disabled={!profileCompletion.isComplete}
                            className={`px-3 sm:px-[18px] py-2 sm:py-2.5 rounded-full border-2 text-xs sm:text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                              isSelected
                                ? 'bg-white border-white text-[#2b42a3]'
                                : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                            }`}
                          >
                            {language}
                          </button>
                        )
                      })
                    ) : (
                      <span className="text-white/60 text-xs">No languages available</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Apply Filters Button - Always visible when filters panel is open */}
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Active Filters Summary */}
                  <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    {hasActiveFilters ? (
                      <>
                        <span className="text-white/80 text-sm">Active filters:</span>
                        {selectedExpertise.map(skill => (
                          <span
                            key={skill}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#2b42a3] text-xs font-semibold"
                          >
                            {skill}
                            <button 
                              onClick={() => profileCompletion.isComplete && toggleExpertise(skill)} 
                              disabled={!profileCompletion.isComplete}
                              className="hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {selectedAvailability.map(id => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#2b42a3] text-xs font-semibold"
                          >
                            {AVAILABILITY_OPTIONS.find(o => o.id === id)?.label}
                            <button 
                              onClick={() => profileCompletion.isComplete && toggleAvailability(id)} 
                              disabled={!profileCompletion.isComplete}
                              className="hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {selectedRateRanges.map(id => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#2b42a3] text-xs font-semibold"
                          >
                            {RATE_RANGES.find(r => r.id === id)?.label}
                            <button 
                              onClick={() => profileCompletion.isComplete && toggleRateRange(id)} 
                              disabled={!profileCompletion.isComplete}
                              className="hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {selectedLanguages.map(language => (
                          <span
                            key={language}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-[#2b42a3] text-xs font-semibold"
                          >
                            {language}
                            <button 
                              onClick={() => profileCompletion.isComplete && toggleLanguage(language)} 
                              disabled={!profileCompletion.isComplete}
                              className="hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </>
                    ) : (
                      <span className="text-white/60 text-sm">Select filters above to refine your search</span>
                    )}
                  </div>

                  {/* Apply Filters Button */}
                  <button
                    onClick={handleApplyFilters}
                    disabled={isSearching || !profileCompletion.isComplete}
                    className="flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-white hover:bg-slate-100 text-[#2b42a3] font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:ml-4 flex-shrink-0 w-full sm:w-auto"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4" />
                        {hasActiveFilters ? 'Apply Filters' : 'Search All Experts'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </SlideIn>

        {/* Search Results Section */}
        {searchResults && (
          <FadeIn>
            <div className="pb-8 sm:pb-12">
              {isSearching ? (
                <div className="text-center py-12 sm:py-16">
                  <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin mx-auto text-[#4669ed] mb-4" />
                  <p className="text-slate-500 text-base sm:text-lg px-4">Searching for AI experts matching your requirements...</p>
                </div>
              ) : searchResults.results.length === 0 ? (
                <div className="text-center py-12 sm:py-16">
                  <p className="text-slate-500 text-base sm:text-lg px-4">No matching AI experts found. Try adjusting your search criteria.</p>
                </div>
              ) : (
                <>
                  <SlideIn direction="up">
                    <h2 className="text-2xl sm:text-[32px] font-black text-slate-900 mb-6 sm:mb-8">Search Results</h2>
                  </SlideIn>
                  <StaggerContainer staggerDelay={0.08} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                  {searchResults.results.map((genius) => (
                    <StaggerItem key={genius.id} className="min-w-0">
                      <FreelancerCard
                        freelancer={genius as typeof genius & { [key: string]: unknown }}
                        showSecondCheckIcon={false}
                        clickable={true}
                      />
                    </StaggerItem>
                  ))}
                  </StaggerContainer>
                </>
              )}
            </div>
          </FadeIn>
        )}

        {/* Featured Freelancers Section - Only show when no active search */}
        {!hasResults && (
          <div className="pb-12 sm:pb-20">
            <ViewportReveal>
              <div className="text-center mb-6 sm:mb-8">
                <SlideIn direction="up">
                  <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-black text-slate-900 leading-tight tracking-tight mb-2 sm:mb-3 px-2">
                    Explore Our Featured Geniuses
                  </h2>
                </SlideIn>
                <FadeIn delay={0.1}>
                  <p className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-[600px] mx-auto px-4">
                    Discover top-rated AI experts ready to transform your business with cutting-edge solutions
                  </p>
                </FadeIn>
              </div>
            </ViewportReveal>

            {/* Category Filter Tabs - Dynamic from freelancer skills */}
            <StaggerContainer staggerDelay={0.05} delayChildren={0.2} className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-8 sm:mb-12 px-2">
              {categoryFilters.map((category) => {
                const isActive = selectedCategory === category.id
                return (
                  <StaggerItem key={category.id}>
                    <button
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-[22px] py-2 sm:py-3 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all ${
                        isActive
                          ? 'bg-[#4669ed] border-[#4669ed] text-white'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      {category.label}
                    </button>
                  </StaggerItem>
                )
              })}
            </StaggerContainer>

            {/* Freelancer Cards Grid */}
            <StaggerContainer staggerDelay={0.08} delayChildren={0.3} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {isLoadingFeatured ? (
                // Loading skeleton cards
                Array.from({ length: 3 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl sm:rounded-[32px] border border-slate-200 p-5 sm:p-7 lg:p-[43px] animate-pulse overflow-hidden"
                    style={{
                      background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                    }}
                  >
                    <div className="flex gap-3 sm:gap-4 mb-4 min-w-0">
                      <div className="w-12 h-12 sm:w-[65px] sm:h-[65px] rounded-2xl sm:rounded-[32px] bg-slate-200 flex-shrink-0" />
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="h-5 sm:h-6 w-3/4 bg-slate-200 rounded" />
                        <div className="h-3 sm:h-4 w-1/2 bg-slate-200 rounded" />
                      </div>
                    </div>
                    <div className="h-3 sm:h-4 w-full bg-slate-200 rounded mb-2" />
                    <div className="h-3 sm:h-4 w-2/3 bg-slate-200 rounded mb-4" />
                    <div className="flex flex-wrap gap-2">
                      <div className="h-7 sm:h-8 w-16 sm:w-20 bg-slate-200 rounded-full" />
                      <div className="h-7 sm:h-8 w-12 sm:w-16 bg-slate-200 rounded-full" />
                      <div className="h-7 sm:h-8 w-20 sm:w-24 bg-slate-200 rounded-full" />
                    </div>
                  </div>
                ))
              ) : filteredFeaturedFreelancers.length === 0 ? (
                <div className="col-span-full text-center py-16">
                  <p className="text-slate-500 text-lg">No featured Geniuses found for this category.</p>
                </div>
              ) : filteredFeaturedFreelancers.map((freelancer) => (
                <StaggerItem key={freelancer.id || 'unknown'} className="min-w-0">
                  <FreelancerCard
                    freelancer={freelancer}
                    showSecondCheckIcon={false}
                    clickable={false}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>

            <FadeIn delay={0.5}>
              <div className="flex justify-center mt-8 sm:mt-12">
                <button
                  onClick={() => navigate('/client/featured')}
                  className="px-8 sm:px-[50px] py-4 sm:py-[22px] rounded-full sm:rounded-[52px] border-2 border-slate-200 bg-white text-[#4669ed] font-bold hover:bg-slate-50 transition-colors text-sm sm:text-base"
                >
                  Load More Geniuses
                </button>
              </div>
            </FadeIn>
          </div>
        )}
      </div>
    </div>
  )
}
